import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import toast from 'react-hot-toast';
import {
  edrService,
  AgentType,
  CompanyOption,
  LicenseAgentList,
  LicenseSummary,
  MdrEvent,
  PolicyStatusRow,
  ReportSummary,
  RiskDistribution,
} from '../services/edr.service';
import { pdfJobsService } from '../services/pdfJobs.service';
import { formatPeriod } from '../lib/edrPeriod';
import { useAuth } from '../contexts/AuthContext';
import ApprovalBox from '../components/report/ApprovalBox';
import { useApproval } from '../components/report/useApproval';
// 공통 규칙을 먼저 싣고, 페이지 전용 규칙이 뒤에서 덮도록 한다
import './EdrShared.css';
import './EdrReportPage.css';

/**
 * EDR 운영보고서 — PRD [S3]~[S5] · [S8]·[S9]
 *
 * 탭 순서는 요구사항 원문을 그대로 따른다.
 *   종합의견 → 정책 현황(정책 변경 이력 포함) → PC 라이선스 → 서버 라이선스
 *
 * PDF 모드(`?pdf=true`)에서는 전 탭을 세로로 펼쳐 렌더하고,
 * **모든 섹션이 로드된 뒤** `window.__pdfDataLoaded`를 세운다.
 * 일찍 세우면 Puppeteer가 빈 표를 찍는다.
 */

type TabId = 'summary' | 'policy' | 'license-pc' | 'license-server';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'summary', label: '종합의견' },
  { id: 'policy', label: '정책 현황' },
  { id: 'license-pc', label: 'EPP(EDR) PC 라이선스 현황' },
  { id: 'license-server', label: 'EPP(EDR) 서버 라이선스 현황' },
];

const POLICY_GROUPS: Array<{ key: 'EPP' | 'V3' | 'EDR'; title: string; note?: string }> = [
  { key: 'EPP', title: '3.1 EPP 정책' },
  { key: 'V3', title: '3.2 V3 정책' },
  { key: 'EDR', title: '3.3 EDR 정책', note: '※ EDR 정책 콘솔 설정 기준, 주요 항목 발췌' },
];

const RISK_CLASS: Record<string, string> = {
  높음: 'badge-risk-high',
  중간: 'badge-risk-mid',
  낮음: 'badge-risk-low',
};

const STORAGE_KEY = 'edr-report-filters';

/**
 * 라이선스 수량 — 배분·반씩 나눈 계약 때문에 `.5`가 나온다.
 * 소수일 때만 구분기호를 빼면 한 표 안에서 `1,426`과 `1426.5`가 섞인다.
 */
const formatAmount = (value: number | null): string => {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('ko-KR', { maximumFractionDigits: 1 });
};

const EdrReportPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('summary');
  const [isPdfMode, setIsPdfMode] = useState(false);

  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPublished, setIsPublished] = useState<boolean | null>(null);

  const [summary, setSummary] = useState<ReportSummary>({ summaryText: '', policyHistory: '' });
  const [distribution, setDistribution] = useState<RiskDistribution | null>(null);
  const [mdrEvents, setMdrEvents] = useState<MdrEvent[]>([]);
  const [policies, setPolicies] = useState<PolicyStatusRow[]>([]);
  const [licensePc, setLicensePc] = useState<LicenseSummary | null>(null);
  const [licenseServer, setLicenseServer] = useState<LicenseSummary | null>(null);
  const [agentsPc, setAgentsPc] = useState<LicenseAgentList | null>(null);
  const [agentsServer, setAgentsServer] = useState<LicenseAgentList | null>(null);
  const [pcPage, setPcPage] = useState(1);
  const [serverPage, setServerPage] = useState(1);

  /**
   * 기간 목록은 **서버가 준다** — 데이터가 있는 달만 골라야 한다.
   * 최근 12개월을 나열하면 EDR 도입 이전 달까지 선택 가능해져 빈 보고서를 보게 된다.
   */
  const [periods, setPeriods] = useState<string[]>([]);
  const [isPeriodsLoaded, setIsPeriodsLoaded] = useState(false);

  // ── 초기화 ─────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pdfParam = params.get('pdf');
    const periodParam = params.get('period');
    const companyParam = params.get('company');

    if (pdfParam === 'true') {
      setIsPdfMode(true);
      (window as any).__pdfDataLoaded = false;
      (window as any).__pdfIsLoading = true;

      if (periodParam) setSelectedPeriod(periodParam);
      if (companyParam) setSelectedCompany(decodeURIComponent(companyParam));
    } else {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const stored = JSON.parse(raw) as { companyName?: string; period?: string };
          if (stored.companyName) setSelectedCompany(stored.companyName);
          if (stored.period) setSelectedPeriod(stored.period);
        }
      } catch {
        // 저장값이 깨져 있으면 기본값으로 시작한다
      }
    }

    edrService
      .getCompanies()
      .then(setCompanies)
      .catch(() => setError('회사 목록을 불러오지 못했습니다.'));

    edrService
      .getAvailablePeriods()
      .then(setPeriods)
      .catch(() => setError('조회 가능한 기간을 불러오지 못했습니다.'))
      .finally(() => setIsPeriodsLoaded(true));
  }, []);

  useEffect(() => {
    if (isPdfMode) return;
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ companyName: selectedCompany, period: selectedPeriod }),
      );
    } catch {
      // 저장 실패는 기능에 영향을 주지 않는다
    }
  }, [selectedCompany, selectedPeriod, isPdfMode]);

  // ── 데이터 로드 ────────────────────────────────────────────
  const loadReport = useCallback(async (period: string, company: string) => {
    setIsLoading(true);
    setDataLoaded(false);
    setError(null);

    try {
      const publish = await edrService.getPublishStatus(period, company);
      setIsPublished(publish.isPublished);

      /**
       * 전 섹션을 병렬로 받는다. PDF는 **모두 로드된 뒤** 신호를 세워야 하므로
       * 탭별 지연 로딩을 하지 않는다.
       */
      const [sum, dist, mdr, policy, pc, server, pcAgents, serverAgents] = await Promise.all([
        edrService.getSummary(period, company),
        edrService.getRiskDistribution(period, company),
        edrService.getMdrEvents(period, company),
        edrService.getPolicyStatus(period, company),
        edrService.getLicenseSummary(period, company, 'PC'),
        edrService.getLicenseSummary(period, company, 'SERVER'),
        edrService.getLicenseAgents({ period, companyName: company, agentType: 'PC', page: 1 }),
        edrService.getLicenseAgents({ period, companyName: company, agentType: 'SERVER', page: 1 }),
      ]);

      setSummary(sum);
      setDistribution(dist);
      setMdrEvents(mdr);
      setPolicies(policy);
      setLicensePc(pc);
      setLicenseServer(server);
      setAgentsPc(pcAgents);
      setAgentsServer(serverAgents);
      setPcPage(1);
      setServerPage(1);
      setDataLoaded(true);
    } catch (e: any) {
      // 미공개 보고서는 백엔드가 403으로 막는다 (Publish 게이트)
      if (e?.response?.status === 403) {
        setIsPublished(false);
        setError(null);
      } else {
        setError('보고서를 불러오지 못했습니다.');
      }
      setDataLoaded(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPeriod && selectedCompany) {
      loadReport(selectedPeriod, selectedCompany);
    } else {
      setDataLoaded(false);
    }
  }, [selectedPeriod, selectedCompany, loadReport]);

  const { approval, settled: approvalSettled } = useApproval(
    selectedPeriod && selectedCompany
      ? { pageType: 'EDR_REPORT', period: selectedPeriod, companyName: selectedCompany }
      : null,
  );

  /** Puppeteer가 이 신호를 기다린다 — 전 섹션 + 결재란 로드 후에만 true가 된다 */
  useEffect(() => {
    if (!isPdfMode) return;
    (window as any).__pdfDataLoaded = dataLoaded && approvalSettled;
    (window as any).__pdfIsLoading = isLoading;
  }, [isPdfMode, dataLoaded, approvalSettled, isLoading]);

  const loadAgentPage = async (agentType: AgentType, page: number) => {
    if (!selectedPeriod || !selectedCompany) return;
    const result = await edrService.getLicenseAgents({
      period: selectedPeriod,
      companyName: selectedCompany,
      agentType,
      page,
    });
    if (agentType === 'PC') {
      setAgentsPc(result);
      setPcPage(page);
    } else {
      setAgentsServer(result);
      setServerPage(page);
    }
  };

  /**
   * PDF는 **비동기 잡 폴링**으로 만든다 (DLP·NAC·유해차단과 동일 경로).
   *
   * 링크를 새 탭으로 열면 Authorization 헤더가 실리지 않아 401이 된다.
   * 생성은 수십 초가 걸리므로 경과 시간을 토스트로 알린다.
   */
  const handleDownloadPdf = async () => {
    if (!selectedPeriod || !selectedCompany) return;

    const period = selectedPeriod;
    const company = selectedCompany;
    const toastId = toast.loading('PDF 작업 시작 중...');

    try {
      const { jobId } = await pdfJobsService.createJob('edr', period, company);
      toast.loading('PDF 생성 중... 0초 경과 (백그라운드 처리, 다른 메뉴로 이동해도 됩니다)', {
        id: toastId,
      });

      const blob = await pdfJobsService.pollUntilDone(jobId, (elapsed) => {
        toast.loading(
          `PDF 생성 중... ${elapsed}초 경과 (백그라운드 처리, 다른 메뉴로 이동해도 됩니다)`,
          { id: toastId },
        );
      });

      // 파일명은 백엔드 buildFileName과 같은 규칙을 쓴다
      const fileName = `[${company}] ${period.slice(0, 4)}년 ${period.slice(4)}월 EDR 솔루션 운영보고서.pdf`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF 다운로드 완료', { id: toastId });
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.message || 'PDF 생성에 실패했습니다.';
      toast.error(`PDF 생성 실패: ${message}`, { id: toastId });
    }
  };

  // ── 섹션 렌더 ──────────────────────────────────────────────
  const renderSummaryTab = () => (
    <div className="tab-panel">
      <div className="report-approval-header">
        <h2>📄 종합의견</h2>
        <ApprovalBox approval={approval} />
      </div>
      <hr className="section-divider" />

      <div className="summary-content">
        {summary.summaryText ? (
          <div className="markdown-content"><ReactMarkdown remarkPlugins={[remarkBreaks]}>{summary.summaryText}</ReactMarkdown></div>
        ) : (
          <span className="text-muted">작성된 종합의견이 없습니다.</span>
        )}
      </div>

      <div className="section-block">
        <div className="section-header">
          <span className="section-icon">📋</span>
          <span className="edr-section-title">위험도별 분포</span>
        </div>
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th className="cell-center">높음</th>
                <th className="cell-center">중간</th>
                <th className="cell-center">낮음</th>
                <th className="cell-center">합계</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="cell-center risk-high">
                  {distribution?.high ?? 0}
                </td>
                <td className="cell-center risk-mid">
                  {distribution?.medium ?? 0}
                </td>
                <td className="cell-center risk-low">
                  {distribution?.low ?? 0}
                </td>
                <td className="cell-center risk-total">
                  {distribution?.total ?? 0}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="section-block">
        <div className="section-header">
          <span className="section-icon">📋</span>
          <span className="edr-section-title">MDR 이벤트 상세 내역</span>
        </div>
        <p className="hint">
          ※ 처리 대상 이벤트 전체 상세 (탐지일자 / 위험도 / 진단명 / 행위 / IP / 처리)
        </p>
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th className="cell-center" style={{ width: '56px' }}>No</th>
                <th className="cell-center">탐지일자</th>
                <th className="cell-center">위험도</th>
                <th>진단명</th>
                <th>행위</th>
                <th className="cell-center">IP</th>
                <th className="cell-center">처리</th>
              </tr>
            </thead>
            <tbody>
              {mdrEvents.length === 0 && (
                <tr className="empty-row">
                  <td colSpan={7}>데이터가 없습니다.</td>
                </tr>
              )}
              {mdrEvents.map((event) => (
                <tr key={event.no}>
                  <td className="cell-center">{event.no}</td>
                  <td className="cell-center">{event.detectedDate}</td>
                  <td className="cell-center">
                    <span className={`inline-badge ${RISK_CLASS[event.riskLevel] ?? ''}`}>
                      {event.riskLevel}
                    </span>
                  </td>
                  <td className="mono">{event.detectionScenario ?? '—'}</td>
                  <td>{event.detectionNote ?? '—'}</td>
                  <td className="mono cell-center">
                    {event.assetIp ?? '—'}
                  </td>
                  <td className="text-error cell-center cell-strong">
                    {event.action}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderPolicyTab = () => (
    <div className="tab-panel">
      <h2>🛡️ 정책 현황</h2>
      <hr className="section-divider" />

      {POLICY_GROUPS.map((group) => {
        const rows = policies.filter((p) => p.policyGroup === group.key);
        return (
          <div className="section-block" key={group.key}>
            <div className="section-header">
              <span className="section-icon">📋</span>
              <span className="edr-section-title">{group.title}</span>
            </div>
            {group.note && <p className="hint">{group.note}</p>}
            <div className="policy-table-container">
              <table className="policy-table">
                <thead>
                  <tr>
                    <th style={{ width: '26%' }}>정책 항목</th>
                    <th>설정 내용</th>
                    <th className="cell-center" style={{ width: '110px' }}>적용 상태</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr className="empty-row">
                      <td colSpan={3}>데이터가 없습니다.</td>
                    </tr>
                  )}
                  {rows.map((row) => (
                    <tr key={row.policyCode}>
                      <td>{row.policyName}</td>
                      <td>{row.policyDesc}</td>
                      <td className="cell-center">
                        <span
                          className={`inline-badge ${row.isApplied ? 'badge-active' : 'badge-inactive'}`}
                        >
                          {row.isApplied ? '적용' : '미적용'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <div className="section-block">
        <div className="section-header">
          <span className="section-icon">📋</span>
          <span className="edr-section-title">3.4 정책 변경 이력</span>
        </div>
        <div className="summary-content" style={{ marginBottom: 0 }}>
          {summary.policyHistory ? (
            <div className="markdown-content"><ReactMarkdown remarkPlugins={[remarkBreaks]}>{summary.policyHistory}</ReactMarkdown></div>
          ) : (
            <span className="text-muted">작성된 변경 이력이 없습니다.</span>
          )}
        </div>
      </div>
    </div>
  );

  const renderLicenseTab = (agentType: AgentType) => {
    const license = agentType === 'PC' ? licensePc : licenseServer;
    const agents = agentType === 'PC' ? agentsPc : agentsServer;
    const page = agentType === 'PC' ? pcPage : serverPage;
    const title =
      agentType === 'PC' ? '🔑 EPP(EDR) PC 라이선스 현황' : '🔑 EPP(EDR) 서버 라이선스 현황';

    const totalPages = agents ? Math.max(1, Math.ceil(agents.total / agents.pageSize)) : 1;
    const unsortable = agents?.unsortableColumns ?? [];

    return (
      <div className="tab-panel">
        <h2>{title}</h2>
        <hr className="section-divider" />

        <div className="section-block">
          <div className="section-header">
            <span className="section-icon">📋</span>
            <span className="edr-section-title">라이선스</span>
          </div>
          {/*
            회사 1행만 보여준다. 설치 라이선스에는 배분 결과가 이미 합산돼 있으며
            affiliate 구분·계산 과정은 화면에 노출하지 않는다 (PRD D-9).
          */}
          <div className="policy-table-container">
            <table className="policy-table">
              <thead>
                <tr>
                  <th>회사</th>
                  <th className="cell-center">구매 라이선스</th>
                  <th className="cell-center">설치 라이선스</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{selectedCompany || '—'}</td>
                  <td className="cell-center">
                    {formatAmount(license?.total.purchased ?? 0)}
                  </td>
                  <td className="cell-center">
                    {formatAmount(license?.total.installed ?? 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="section-block">
          <div className="section-header-row">
            <div className="section-header">
              <span className="section-icon">📋</span>
              <span className="edr-section-title">라이선스 현황</span>
            </div>
            <span className="text-muted section-count">
              전체 {(agents?.total ?? 0).toLocaleString()}건
            </span>
          </div>

          <div className="policy-table-container">
            <table className="policy-table edr-license-table">
              <thead>
                <tr>
                  <th>IP주소</th>
                  <th>연결된 IP주소</th>
                  <th>MAC</th>
                  <th>그룹 경로</th>
                  <th>에이전트 타입</th>
                  <th>계열사</th>
                  {/* 암호화 저장 컬럼은 정렬할 수 없다 — 헤더에도 표시하지 않는다 */}
                  <th>부서명</th>
                  <th>이름</th>
                  <th>이메일</th>
                  <th>컴퓨터 이름</th>
                  <th>운영체제</th>
                </tr>
              </thead>
              <tbody>
                {(!agents || agents.rows.length === 0) && (
                  <tr className="empty-row">
                    <td colSpan={11}>데이터가 없습니다.</td>
                  </tr>
                )}
                {agents?.rows.map((row, index) => (
                  <tr key={`${row.ip ?? 'row'}-${index}`}>
                    <td className="mono">{row.ip ?? '—'}</td>
                    <td className="mono">{row.ipConnected ?? '—'}</td>
                    <td className="mono">{row.mac ?? '—'}</td>
                    <td>{row.namePath ?? '—'}</td>
                    <td>{row.agentType}</td>
                    <td>{row.affiliate ?? '—'}</td>
                    <td>{row.department ?? '—'}</td>
                    <td>{row.name ?? '—'}</td>
                    <td className="mono">{row.email ?? '—'}</td>
                    <td className="mono">{row.computerName ?? '—'}</td>
                    <td>{row.platformId ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!isPdfMode && totalPages > 1 && (
            <div className="pagination">
              <button disabled={page === 1} onClick={() => loadAgentPage(agentType, page - 1)}>
                ‹
              </button>
              <span className="current">
                {page} / {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => loadAgentPage(agentType, page + 1)}
              >
                ›
              </button>
            </div>
          )}

          {unsortable.length > 0 && (
            <p className="footnote">※ 개인정보가 포함된 항목은 정렬을 지원하지 않습니다.</p>
          )}
        </div>
      </div>
    );
  };

  const renderTab = (tab: TabId) => {
    switch (tab) {
      case 'summary':
        return renderSummaryTab();
      case 'policy':
        return renderPolicyTab();
      case 'license-pc':
        return renderLicenseTab('PC');
      case 'license-server':
        return renderLicenseTab('SERVER');
      default:
        return null;
    }
  };

  /**
   * Publish 게이트는 **VIEWER에게만** 걸린다 (DLP·NAC와 동일).
   *
   * 백엔드 `assertViewable`은 ADMIN/EDITOR/REVIEWER를 통과시킨다. 화면이 role을 보지 않으면
   * 담당자가 **발행 전에 보고서를 검토할 수 없다** — 발행 버튼을 누르기 전에 내용을
   * 확인할 방법이 사라진다.
   *
   * PDF 모드는 발행본 생성 경로라 게이트를 적용하지 않는다.
   */
  const isPublishGated = !isPdfMode && user?.role === 'viewer' && isPublished === false;
  const showReport = Boolean(selectedPeriod && selectedCompany) && !isPublishGated;

  return (
    <div className={`edr-report-page ${isPdfMode ? 'pdf-mode' : ''}`}>
      {!isPdfMode && (
        <div className="page-header">
          <h1>EDR 운영보고서</h1>
          <div className="header-controls">
            <div className="filter-group">
              <label htmlFor="edr-report-company">회사:</label>
              <select
                id="edr-report-company"
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={isLoading}
              >
                <option value="">선택하세요</option>
                {companies.map((c) => (
                  <option key={c.orgCode} value={c.companyName}>
                    {c.companyName}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="edr-report-period">기간:</label>
              <select
                id="edr-report-period"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                disabled={!selectedCompany || isLoading}
              >
                <option value="">
                  {selectedCompany ? '선택하세요' : '회사를 먼저 선택하세요'}
                </option>
                {periods.map((p) => (
                  <option key={p} value={p}>
                    {formatPeriod(p)}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="download-button"
              onClick={handleDownloadPdf}
              disabled={!showReport || isLoading}
            >
              PDF 다운로드
            </button>
          </div>
        </div>
      )}

      {!isPdfMode && (
        <div className="tab-container">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {error && <div className="publish-warning">{error}</div>}

      {/* Publish 게이트 — Viewer에게는 공개된 보고서만 보인다 (SC-6) */}
      {selectedPeriod && selectedCompany && isPublishGated && (
        <div className="publish-warning">
          공개되지 않은 보고서입니다. 담당자가 발행한 뒤 조회할 수 있습니다.
        </div>
      )}

      {/*
        발행 상태 배지는 두지 않는다 (사용자 결정 2026-08-24).
        발행 여부는 관리자 화면(`edr-report` 탭)이 표시한다 — 보고서는 열람용이며
        VIEWER에게는 애초에 공개본만 보이므로 배지가 늘 '공개'로 고정된다.
        `isPublished`는 아래 Publish 게이트에서만 쓴다.
      */}
      {isPeriodsLoaded && periods.length === 0 ? (
        <div className="empty-state">
          조회할 수 있는 기간이 없습니다. 수집·발행이 완료된 뒤 표시됩니다.
        </div>
      ) : !selectedPeriod || !selectedCompany ? (
        <div className="empty-state">회사와 기간을 선택하세요.</div>
      ) : isLoading ? (
        <div className="loading">불러오는 중…</div>
      ) : (
        showReport && (
          <div className="tab-content">
            {isPdfMode ? (
              <>
                <div className="pdf-page-break pdf-cover-page">
                  <div className="cover-content">
                    <h1 className="cover-title">EDR 솔루션 운영보고서</h1>
                    <h2 className="cover-company">[{selectedCompany}]</h2>
                    <p className="cover-period">{formatPeriod(selectedPeriod)}</p>
                  </div>
                </div>
                {TABS.map((tab) => (
                  <div className="pdf-page-break" key={tab.id}>
                    {renderTab(tab.id)}
                  </div>
                ))}
              </>
            ) : (
              renderTab(activeTab)
            )}
          </div>
        )
      )}
    </div>
  );
};

export default EdrReportPage;
